/**
 * Subdomain detection utility.
 * Runs before React Router mounts to determine if the current hostname
 * is a subdomain of ashlarapp.com (e.g., smith.ashlarapp.com).
 */

const APP_DOMAIN = 'ashlarapp.com';

/** Subdomains reserved for platform use — cannot be claimed by appraisers */
const RESERVED_SUBDOMAINS = new Set([
  'www', 'app', 'api', 'admin', 'blog', 'support', 'mail',
  'staging', 'dev', 'test', 'status', 'help', 'docs',
]);

export interface SubdomainInfo {
  isSubdomainSite: boolean;
  subdomain: string | null;
  /** Set when accessing via a custom domain (e.g., smithappraisals.com) */
  customDomain?: string;
}

/**
 * Detect if the current page is being served from an appraiser subdomain.
 *
 * Production: `smith.ashlarapp.com` → { isSubdomainSite: true, subdomain: 'smith' }
 * Main domain: `ashlarapp.com` → { isSubdomainSite: false, subdomain: null }
 * Dev/preview: Uses path-based fallback since localhost doesn't support subdomains.
 */
export function getSubdomainInfo(): SubdomainInfo {
  const hostname = window.location.hostname;

  // ── Production: *.ashlarapp.com ──
  if (hostname.endsWith(`.${APP_DOMAIN}`)) {
    const sub = hostname.slice(0, -(APP_DOMAIN.length + 1)); // strip ".ashlarapp.com"
    if (sub && !sub.includes('.') && !RESERVED_SUBDOMAINS.has(sub)) {
      return { isSubdomainSite: true, subdomain: sub };
    }
  }

  // ── Custom domain: any hostname that isn't ashlarapp.com or localhost ──
  if (
    hostname !== APP_DOMAIN &&
    !hostname.endsWith(`.${APP_DOMAIN}`) &&
    hostname !== 'localhost' &&
    !hostname.endsWith('.localhost')
  ) {
    // This is a custom domain — mark as subdomain site.
    // The actual subdomain will be resolved by looking up the custom_domain
    // in the database at render time (SubdomainSiteRouter handles this).
    return { isSubdomainSite: true, subdomain: null, customDomain: hostname };
  }

  // ── Root domain or reserved subdomain — not a site ──
  return { isSubdomainSite: false, subdomain: null };
}

/** Check if a subdomain string is reserved */
export function isReservedSubdomain(subdomain: string): boolean {
  return RESERVED_SUBDOMAINS.has(subdomain.toLowerCase());
}
